import { RefObject, useMemo, useRef, useEffect, MutableRefObject } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { Virtual } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css';
import 'swiper/css/virtual';

import { CarouselItem } from './CarouselItem';
import { TimelineCarouselWrapper } from './SwiperCustom.styles';

import { useIsMobile } from '@/hooks';
import { useMyPlanification } from '@/store';

type Props = {
  carouselRef: RefObject<SwiperType | null>;
  planificationDates: string[];
  setCurrentSlide: (slide: number) => void;
  selectedDate: string;
  onDateClick: (date: string) => void;
};

const MOBILE_SLIDES = 3;
const DEFAULT_SLIDES = 7;

export const Carousel: React.FC<Props> = ({
  carouselRef,
  planificationDates,
  setCurrentSlide,
  selectedDate,
  onDateClick
}) => {
  const isMobile = useIsMobile('md');
  const swiperRef = useRef<SwiperType | null>(null);

  const { sortedPlanificationActivitiesByDate } = useMyPlanification();

  const handleClick = (date: string) => {
    onDateClick(date);
  };

  useEffect(() => {
    if (swiperRef.current && carouselRef) {
      (carouselRef as MutableRefObject<SwiperType | null>).current = swiperRef.current;
    }
    return () => {
      if (carouselRef) {
        (carouselRef as MutableRefObject<SwiperType | null>).current = null;
      }
      swiperRef.current = null;
    };
  }, [carouselRef]);

  const virtualSlides = useMemo(() => {
    return planificationDates.map((date, index) => ({
      date,
      index
    }));
  }, [planificationDates]);

  const slidesSize = isMobile ? MOBILE_SLIDES : DEFAULT_SLIDES;

  return (
    <TimelineCarouselWrapper>
      <Swiper
        modules={[Virtual]}
        onSwiper={(swiper: SwiperType) => {
          swiperRef.current = swiper;
          if (carouselRef) {
            (carouselRef as MutableRefObject<SwiperType | null>).current = swiper;
          }
        }}
        onDestroy={() => {
          if (carouselRef) {
            (carouselRef as MutableRefObject<SwiperType | null>).current = null;
          }
          swiperRef.current = null;
        }}
        spaceBetween={isMobile ? 4 : 12}
        slidesPerView={slidesSize}
        slidesPerGroup={slidesSize}
        virtual={{
          enabled: true,
          slides: virtualSlides
        }}
        watchSlidesProgress={true}
        allowTouchMove={false}
        loop={false}
        onSlideChange={(swiper: SwiperType) => setCurrentSlide(swiper.activeIndex)}>
        {virtualSlides.map((slideData, index) => (
          <SwiperSlide key={slideData.date} virtualIndex={index}>
            <CarouselItem
              date={slideData.date}
              onClick={handleClick}
              activities={sortedPlanificationActivitiesByDate[slideData.date]}
              isSelected={slideData.date === selectedDate}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </TimelineCarouselWrapper>
  );
};
